/* Dawn continues to own variant resolution, cart requests and checkout. */
(() => {
  const sizeChoices = new WeakMap();
  const stateFor = (info) => {
    const productId = info.querySelector('variant-selects')?.dataset.productId;
    let state = sizeChoices.get(info);
    if (!state || state.productId !== productId) {
      state = { productId, selected: new Set(), initialized: new WeakSet() };
      sizeChoices.set(info, state);
    }
    return state;
  };
  const refreshSizeState = (info) => {
    const pickers = [...info.querySelectorAll('[data-nl-size-choice]')];
    if (!pickers.length) return;
    const state = stateFor(info);
    const chosen = String(pickers.every((picker) => state.selected.has(picker.dataset.nlSizeChoice)));
    if (info.dataset.nlSizeChosen !== chosen) info.dataset.nlSizeChosen = chosen;
  };
  const initialize = (root = document) => {
    root.querySelectorAll('product-info').forEach((info) => {
      const pickers = info.querySelectorAll('[data-nl-size-choice]');
      if (!pickers.length) return;
      const state = stateFor(info);
      pickers.forEach((picker) => {
        if (state.initialized.has(picker)) return;
        state.initialized.add(picker);
        if (state.selected.has(picker.dataset.nlSizeChoice)) return;
        picker.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
        const select = picker.querySelector('select');
        if (select) {
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = `Choose ${picker.dataset.nlSizeChoice.toLowerCase()}`;
          placeholder.disabled = true;
          placeholder.selected = true;
          select.prepend(placeholder);
          select.value = '';
        }
        if (info.querySelector('.nl-express') && !picker.querySelector('.nl-size-hint')) {
          const hint = document.createElement('p');
          hint.className = 'nl-size-hint';
          hint.textContent = `Select your ${picker.dataset.nlSizeChoice.toLowerCase()} for express checkout.`;
          picker.append(hint);
        }
      });
      refreshSizeState(info);
    });
  };
  document.addEventListener('change', (event) => {
    const info = event.target.closest('product-info');
    if (!info) return;
    if (event.target.matches('input.product-variant-id')) { info.dataset.nlVariantPending = 'false'; return; }
    if (!event.target.closest('variant-selects')) return;
    info.dataset.nlVariantPending = 'true';
    // Never leave the previous board variant's stock disclosure visible while
    // Dawn resolves a new selection (including a nonexistent combination).
    const boardNotice = info.querySelector('[data-nl-board-purchase]');
    if (boardNotice) boardNotice.hidden = true;
    const picker = event.target.closest('[data-nl-size-choice]');
    if (!picker || !event.target.matches('input, select') || !event.target.value) return;
    stateFor(info).selected.add(picker.dataset.nlSizeChoice);
    refreshSizeState(info);
    picker.querySelector('.nl-size-hint')?.remove();
    info.querySelector('.nl-size-message')?.remove();
  }, true);
  // Dawn updates the native button's enabled state, then publishes the freshly
  // rendered variant. Copy only its presentation; never enable checkout here.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.html || !data.variant?.id) return;
      const sourceInfo = data.html.querySelector('product-info');
      const sourceNotice = sourceInfo?.querySelector('[data-nl-board-purchase]');
      const sourceButton = data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
      if (!sourceNotice || !sourceButton) return;
      document.querySelectorAll('product-info').forEach((info) => {
        if ((info.dataset.originalSection || info.dataset.section) !== String(data.sectionId)) return;
        if (info.dataset.productId !== sourceInfo.dataset.productId) return;
        const form = info.querySelector('product-form');
        const variantInput = form?.querySelector('input.product-variant-id');
        if (variantInput?.value !== String(data.variant.id)) return;
        if (sourceNotice.dataset.variantId !== String(data.variant.id)) return;
        const notice = form.querySelector('[data-nl-board-purchase]');
        const button = form.querySelector('button[name="add"]');
        if (!notice || !button) return;
        notice.innerHTML = sourceNotice.innerHTML;
        notice.dataset.state = sourceNotice.dataset.state;
        notice.dataset.variantId = sourceNotice.dataset.variantId;
        notice.hidden = sourceNotice.hidden;
        // A pending/error-disabled native form must never be made orderable by
        // an event from another product instance (for example a quick-add).
        if (info.dataset.nlVariantPending === 'true' || button.disabled !== sourceButton.disabled) {
          notice.hidden = true;
          return;
        }
        const label = button.querySelector('span');
        const sourceLabel = sourceButton.querySelector('span');
        if (label && sourceLabel) label.textContent = sourceLabel.textContent;
      });
    });
  }
  const promptSize = (info) => {
    const state = stateFor(info);
    const picker = [...info.querySelectorAll('[data-nl-size-choice]')].find((item) => !state.selected.has(item.dataset.nlSizeChoice));
    if (!picker) return;
    if (!picker.querySelector('.nl-size-message')) {
      const message = document.createElement('p');
      message.className = 'nl-size-message';
      message.setAttribute('role', 'alert');
      message.textContent = 'Please choose your size.';
      picker.append(message);
    }
    picker.scrollIntoView({ block: 'center', behavior: 'smooth' });
    picker.querySelector('input:not(.disabled):not([disabled]), select:not([disabled])')?.focus({ preventScroll: true });
  };
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
    const info = event.target.closest('product-info');
    if (info?.dataset.nlSizeChosen !== 'false' && info?.dataset.nlVariantPending !== 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (info.dataset.nlSizeChosen === 'false') promptSize(info);
  }, true);
  initialize();
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; initialize(); });
  }).observe(document.body, { childList: true, subtree: true });
  // One mobile action, connected to the real form rather than a second cart flow.
  const main = document.querySelector('product-info[id^="MainProduct-"]');
  const realButton = main?.querySelector('product-form button[name="add"]');
  if (main && realButton && 'IntersectionObserver' in window) {
    const bar = document.createElement('div');
    bar.className = 'nl-sticky-purchase';
    bar.hidden = true;
    const summary = document.createElement('div');
    summary.className = 'nl-sticky-purchase__summary';
    const title = document.createElement('span');
    title.className = 'nl-sticky-purchase__title';
    title.textContent = main.querySelector('h1')?.textContent.trim() || 'Your selection';
    const selection = document.createElement('span');
    selection.className = 'nl-sticky-purchase__selection';
    const price = document.createElement('span');
    price.className = 'nl-sticky-purchase__price';
    summary.append(title, selection, price);
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'button button--primary';
    action.textContent = 'Add to cart';
    action.addEventListener('click', () => {
      const currentButton = main.querySelector('product-form button[name="add"]');
      if (main.dataset.nlVariantPending === 'true') return;
      if (main.dataset.nlSizeChosen === 'false') { promptSize(main); return; }
      if (!currentButton || currentButton.disabled) { main.querySelector('variant-selects')?.scrollIntoView({ block: 'center' }); return; }
      currentButton.click();
    });
    bar.append(summary, action);
    document.body.append(bar);
    const update = () => {
      const current = main.querySelector('product-form button[name="add"]');
      action.textContent = current?.innerText.trim() || 'Add to cart';
      action.disabled = main.dataset.nlVariantPending === 'true' || (main.dataset.nlSizeChosen !== 'false' && !!current?.disabled);
      const pending = main.dataset.nlVariantPending === 'true';
      const sizePickers = [...main.querySelectorAll('[data-nl-size-choice]')];
      const choices = sizePickers.map((picker) => {
        const chosen = picker.querySelector('input:checked, select');
        const value = chosen?.value;
        return value ? `${picker.dataset.nlSizeChoice}: ${value}` : '';
      }).filter(Boolean);
      selection.textContent = pending ? 'Updating selection…' : main.dataset.nlSizeChosen === 'false' ? 'Choose your size' : choices.join(' · ');
      selection.hidden = !selection.textContent;
      const priceBlock = main.querySelector('[id^="price-"] .price');
      const priceSelector = priceBlock?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
      price.textContent = pending ? '' : priceBlock?.querySelector(priceSelector)?.textContent.trim() || '';
      price.hidden = !price.textContent;
    };
    new MutationObserver(update).observe(main, { attributes: true, childList: true, subtree: true });
    new IntersectionObserver(([entry]) => { bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0; update(); }).observe(realButton);
  }
})();
